/* External Deps */
import html2pdf from 'html2pdf.js';
import { generateCertificateHTML, ImprovedCertificateGenerator } from './certificateTemplate';
import { TextSelectablePDFGenerator } from './textSelectablePDFGenerator';
import { EnhancedTextSelectablePDFGenerator } from './enhancedTextSelectablePDFGenerator';

// PDF generation utility using html2pdf.js
export class CertificateGenerator {
  static async generatePDFAlternative(certificateData: FullCertificateData): Promise<void> {
    try {
      // Generate the SAME HTML content as preview
      const htmlContent = generateCertificateHTML(certificateData);
      
      // Create invisible iframe
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '8.5in';
      iframe.style.height = '11in';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      // Write content to iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error('Unable to access iframe document');
      }

      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      // Wait for iframe content to load
      await new Promise<void>((resolve) => {
        iframe.onload = () => {
          setTimeout(resolve, 1000); // Extra time for styling
        };
        // Fallback in case onload doesn't fire
        setTimeout(resolve, 2000);
      });

      // Get the certificate container from iframe
      const certificateContainer = iframeDoc.querySelector('.certificate-container');
      if (!certificateContainer) {
        throw new Error('Certificate container not found in iframe');
      }

      // PDF options optimized for multi-page documents
      const options = {
        margin: [0.5, 0.5, 0.5, 0.5] as [number, number, number, number], // top, right, bottom, left
        filename: `CUSMA_Certificate_${certificateData._id || 'Document'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: false,
          foreignObjectRendering: false,
          removeContainer: false,
          scrollX: 0,
          scrollY: 0,
          width: null, // Let it auto-size
          height: null, // Let it auto-size  
          windowWidth: 850,
          windowHeight: 1400 // Taller window for multi-page content
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      console.log('Generating PDF from iframe content');

      // Generate PDF from iframe content
      await html2pdf()
        .from(certificateContainer as HTMLElement)
        .set(options)
        .save();

      // Clean up
      document.body.removeChild(iframe);
      console.log('PDF generation completed (iframe method)');

    } catch (error) {
      console.error('Error generating certificate PDF (iframe method):', error);
      throw error;
    }
  }
  static async generatePDF(certificateData: FullCertificateData): Promise<void> {
    try {
      // Generate the SAME HTML content as preview
      const htmlContent = generateCertificateHTML(certificateData);
      
      // Extract CSS styles and body content from the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const styleElement = doc.querySelector('style');
      const bodyContent = doc.querySelector('.certificate-container');
      
      if (!styleElement || !bodyContent) {
        throw new Error('Unable to parse certificate HTML content');
      }

      console.log('PDF Generation Debug:', {
        styleLength: styleElement.textContent?.length,
        contentLength: bodyContent.innerHTML?.length,
        hasContent: !!bodyContent.innerHTML
      });

      // Create temporary style element and add to document head
      const tempStyle = document.createElement('style');
      tempStyle.textContent = styleElement.textContent;
      tempStyle.id = 'temp-certificate-styles';
      document.head.appendChild(tempStyle);

      // Create temporary div to hold content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = bodyContent.outerHTML;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '0px'; // Change from -9999px to 0px for debugging
      tempDiv.style.top = '0px';
      tempDiv.style.width = '8.5in';
      tempDiv.style.height = 'auto';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.zIndex = '-1000';
      tempDiv.style.visibility = 'hidden'; // Hide visually but keep in layout
      tempDiv.id = 'temp-certificate-content';
      document.body.appendChild(tempDiv);

      console.log('Temp div created:', {
        hasElement: !!tempDiv,
        children: tempDiv.children.length,
        innerHTML: tempDiv.innerHTML.substring(0, 200) + '...'
      });

      // Wait for styles to be applied and layout to stabilize
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 500); // Give extra time for rendering
        });
      });

      // PDF options optimized for multi-page documents
      const options = {
        margin: [0.5, 0.5, 0.5, 0.5] as [number, number, number, number], // top, right, bottom, left
        filename: `CUSMA_Certificate_${certificateData._id || 'Document'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: false,
          foreignObjectRendering: false,
          removeContainer: false,
          scrollX: 0,
          scrollY: 0,
          width: null, // Let it auto-size
          height: null, // Let it auto-size  
          windowWidth: 850,
          windowHeight: 1400 // Taller window for multi-page content
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      console.log('Starting PDF generation with options:', options);

      // Generate and download PDF
      await html2pdf()
        .from(tempDiv)
        .set(options)
        .save();

      console.log('PDF generation completed');

      // Clean up
      document.body.removeChild(tempDiv);
      document.head.removeChild(tempStyle);

    } catch (error) {
      console.error('Error generating certificate PDF:', error);
      throw error;
    }
  }

  static async generatePDFBlob(certificateData: FullCertificateData): Promise<Blob> {
    try {
      // Use the improved HTML generation
      const htmlContent = ImprovedCertificateGenerator.generateOptimizedHTML(certificateData);
      
      // Create a hidden container
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.overflow = 'auto';
      container.style.zIndex = '-9999';
      container.style.background = 'white';
      container.innerHTML = htmlContent;
      
      document.body.appendChild(container);

      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 100));

      // Optimized options for html2pdf (same as improved generator)
      const options = {
        margin: 0, // We handle margins in CSS
        image: { 
          type: 'jpeg', 
          quality: 1 
        },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: 816, // Fixed width
          windowWidth: 816
        },
        jsPDF: { 
          unit: 'px', 
          format: [816, 1056], // 8.5 x 11 inches in pixels
          orientation: 'portrait',
          compress: false // Better quality
        },
        pagebreak: { 
          mode: ['avoid-all', 'css'], 
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: '.no-break'
        }
      };

      // Get the certificate element
      const element = container.querySelector('.certificate-container');
      if (!element) {
        throw new Error('Certificate container not found');
      }

      // Generate PDF blob
      const pdfBlob = await html2pdf()
        .from(element)
        .set(options)
        .outputPdf('blob');

      // Cleanup
      document.body.removeChild(container);

      return pdfBlob;

    } catch (error) {
      console.error('Error generating certificate PDF blob:', error);
      throw error;
    }
  }

  static async previewCertificate(certificateData: FullCertificateData): Promise<void> {
    try {
      // Generate HTML content
      const htmlContent = generateCertificateHTML(certificateData);
      
      // Create a new window for preview
      const previewWindow = window.open('', '_blank', 'width=850,height=1100,scrollbars=yes');
      
      if (!previewWindow) {
        throw new Error('Popup blocked. Please allow popups for certificate preview.');
      }

      // Write content to the new window
      previewWindow.document.write(htmlContent);
      previewWindow.document.close();

      // Focus the new window
      previewWindow.focus();

    } catch (error) {
      console.error('Error previewing certificate:', error);
      throw error;
    }
  }

  static downloadHTML(certificateData: FullCertificateData): void {
    try {
      // Generate HTML content
      const htmlContent = generateCertificateHTML(certificateData);
      
      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `CUSMA_Certificate_${certificateData._id || 'Document'}.html`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading certificate HTML:', error);
      throw error;
    }
  }
}

// Hook for certificate generation
export const useCertificateGenerator = () => {
  const generatePDF = async (certificateData: FullCertificateData) => {
    // Try the enhanced text-selectable PDF generator first (best option)
    try {
      await EnhancedTextSelectablePDFGenerator.generatePDF(certificateData);
    } catch (error) {
      console.warn('Enhanced text-selectable PDF generator failed, trying basic text-selectable:', error);
      // Fallback to basic text-selectable generator
      try {
        await TextSelectablePDFGenerator.generatePDF(certificateData);
      } catch (textError) {
        console.warn('Text-selectable PDF generator failed, trying improved generator:', textError);
        // Fallback to improved generator
        try {
          await ImprovedCertificateGenerator.generatePDF(certificateData);
        } catch (fallbackError) {
          console.warn('Improved PDF generator failed, trying iframe method:', fallbackError);
          // Fallback to iframe method
          try {
            await CertificateGenerator.generatePDFAlternative(certificateData);
          } catch (finalError) {
            console.warn('Iframe method failed, trying direct DOM method:', finalError);
            await CertificateGenerator.generatePDF(certificateData);
          }
        }
      }
    }
  };

  const generatePDFBlob = async (certificateData: FullCertificateData) => {
    return await CertificateGenerator.generatePDFBlob(certificateData);
  };

  const generateTextSelectablePDF = async (certificateData: FullCertificateData) => {
    // Force enhanced text-selectable PDF generation
    await EnhancedTextSelectablePDFGenerator.generatePDF(certificateData);
  };

  const generateImagePDF = async (certificateData: FullCertificateData) => {
    // Force image-based PDF generation (more visual accuracy)
    try {
      await ImprovedCertificateGenerator.generatePDF(certificateData);
    } catch (error) {
      console.warn('Improved generator failed, trying iframe method:', error);
      await CertificateGenerator.generatePDFAlternative(certificateData);
    }
  };

  const previewCertificate = async (certificateData: FullCertificateData) => {
    await CertificateGenerator.previewCertificate(certificateData);
  };

  const downloadHTML = (certificateData: FullCertificateData) => {
    CertificateGenerator.downloadHTML(certificateData);
  };

  return {
    generatePDF, // Default: tries text-selectable first, then fallbacks
    generateTextSelectablePDF, // Force text-selectable
    generateImagePDF, // Force image-based (better visual quality)
    generatePDFBlob,
    previewCertificate,
    downloadHTML,
  };
};